
import React, { ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { AdminSidebar } from "./AdminSidebar";
import { Header } from "@/components/Header";
import { useUserCount } from "@/hooks/useUserCount";
import { useServiceCount } from "@/hooks/useServiceCount";
import { useCategoryCount } from "@/hooks/useCategoryCount";
import { useRegionCount } from "@/hooks/useRegionCount";
import { useReviewCount } from "@/hooks/useReviewCount";
import { useBookingCount } from "@/hooks/useBookingCount";
import { Users, Server, FolderTree, Globe, MessageSquare, Calendar } from "lucide-react";
import { SidebarProvider, SidebarInset } from "@/components/ui/sidebar";

interface AdminLayoutProps {
  children: ReactNode;
  title: string;
}

export function AdminLayout({ children, title }: AdminLayoutProps) {
  const location = useLocation();
  
  // Use the CountResult object properly
  const { count: userCount } = useUserCount();
  const { count: serviceCount } = useServiceCount();
  const { count: categoryCount } = useCategoryCount();
  const { count: regionCount } = useRegionCount();
  const { count: reviewCount } = useReviewCount();
  const { count: bookingCount } = useBookingCount();

  // Determine the current page
  const isUsersPage = location.pathname.includes("/admin/users");
  const isServicesPage = location.pathname.includes("/admin/services");
  const isCategoriesPage = location.pathname.includes("/admin/categories");
  const isRegionsPage = location.pathname.includes("/admin/regions");
  const isReviewsPage = location.pathname.includes("/admin/reviews");
  const isBookingsPage = location.pathname.includes("/admin/bookings");
  
  // Create the display title with count if needed
  let displayTitle = title;
  let icon = null;

  if (isUsersPage && userCount !== undefined) {
    displayTitle = `${title} (${userCount})`;
    icon = <Users className="h-5 w-5 text-primary" />;
  } else if (isServicesPage && serviceCount !== undefined) {
    displayTitle = `${title} (${serviceCount})`;
    icon = <Server className="h-5 w-5 text-primary" />;
  } else if (isCategoriesPage && categoryCount !== undefined) {
    displayTitle = `${title} (${categoryCount})`;
    icon = <FolderTree className="h-5 w-5 text-primary" />;
  } else if (isRegionsPage && regionCount !== undefined) {
    displayTitle = `${title} (${regionCount})`;
    icon = <Globe className="h-5 w-5 text-primary" />;
  } else if (isReviewsPage && reviewCount !== undefined) {
    displayTitle = `${title} (${reviewCount})`;
    icon = <MessageSquare className="h-5 w-5 text-primary" />;
  } else if (isBookingsPage && bookingCount !== undefined) {
    displayTitle = `${title} (${bookingCount})`;
    icon = <Calendar className="h-5 w-5 text-primary" />;
  }

  return (
    <SidebarProvider>
      <div className="min-h-screen bg-gray-50 flex flex-col w-full">
        {/* Header using the old header component with admin context */}
        <div className="fixed top-0 left-0 right-0 z-40 h-16">
          <Header isAdminPage={true} />
        </div>
        
        {/* Main layout with sidebar - starts after header */}
        <div className="flex flex-1 pt-16">
          <AdminSidebar />
          
          <SidebarInset>
            {/* Main content with proper top margin to clear header completely */}
            <main className="flex-1 overflow-auto p-4 md:p-6" style={{ marginTop: '4rem' }}>
              <div className="flex items-center gap-2 mb-8">
                {icon}
                <h1 className="text-2xl font-bold text-gray-800">{displayTitle}</h1>
              </div>
              {children}
            </main>
          </SidebarInset>
        </div>
      </div>
    </SidebarProvider>
  );
}
